import React from "react";
import { Target, Users, Lightbulb, Award } from "lucide-react";

const AboutSection = () => {
  const values = [
    {
      icon: Target,
      title: "Mission-Driven",
      description: "Democratizing AI to solve real-world challenges across critical industries"
    },
    {
      icon: Users,
      title: "Client-Centric",
      description: "Building lasting partnerships through transparent collaboration and results"
    },
    {
      icon: Lightbulb,
      title: "Innovation First",
      description: "Leveraging cutting-edge research to deliver tomorrow's solutions today"
    },
    {
      icon: Award,
      title: "Excellence Always",
      description: "Maintaining the highest standards in AI development and deployment"
    }
  ];

  return (
    <section id="about" className="py-24 bg-slate-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(6,182,212,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(6,182,212,0.02)_1px,transparent_1px)] bg-[size:80px_80px]"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              About <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-400">SmartShieldGlobal</span>
            </h2>
            <p className="text-xl text-slate-400 leading-relaxed">
              We are a leading AI solutions provider specializing in transforming Financial Services, Healthcare, and Education sectors. With over a decade of experience, we architect secure, intelligent ecosystems that drive measurable business outcomes.
            </p>
          </div>

          {/* Story Section */}
          <div className="grid lg:grid-cols-2 gap-12 mb-16 items-center">
            <div>
              <h3 className="text-3xl font-bold text-white mb-6">Our Story</h3>
              <div className="space-y-4 text-slate-300 leading-relaxed">
                <p>
                  Founded as a subsidiary of SmartShieldAI, we've dedicated ourselves to bringing enterprise-grade AI solutions to organizations that need them most. Our focus on three critical sectors—Financial Services, Healthcare, and Education—allows us to develop deep domain expertise and deliver unparalleled value.
                </p>
                <p>
                  With a team of world-class data scientists, engineers, and industry experts, we've successfully deployed over 500 AI projects that have transformed operations, improved outcomes, and created lasting competitive advantages for our clients.
                </p>
                <p>
                  Our approach combines rigorous research, ethical AI practices, and practical implementation to ensure that every solution we deliver is secure, scalable, and sustainable.
                </p>
              </div>
            </div>

            {/* Stats Card */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
              <h4 className="text-2xl font-bold text-white mb-8">By The Numbers</h4>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { value: "500+", label: "Projects Delivered" },
                  { value: "98%", label: "Client Satisfaction" },
                  { value: "10+", label: "Years Experience" },
                  { value: "50+", label: "Enterprise Clients" },
                  { value: "3", label: "Core Industries" },
                  { value: "24/7", label: "Support Available" }
                ].map((stat, index) => (
                  <div key={index} className="text-center p-4 bg-slate-900/50 rounded-xl border border-slate-700/30">
                    <div className="text-3xl font-bold text-cyan-400 mb-2">{stat.value}</div>
                    <div className="text-sm text-slate-400">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Values Grid */}
          <div>
            <h3 className="text-3xl font-bold text-white text-center mb-12">Our Core Values</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => {
                const IconComponent = value.icon;
                return (
                  <div
                    key={index}
                    className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 hover:border-cyan-500/50 transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 rounded-lg flex items-center justify-center mb-4 border border-cyan-500/30">
                      <IconComponent className="w-6 h-6 text-cyan-400" />
                    </div>
                    <h4 className="text-lg font-bold text-white mb-2">{value.title}</h4>
                    <p className="text-slate-400 text-sm leading-relaxed">{value.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;